// Phase 21 · Rooms directory
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Filter, Mic, Video } from "lucide-react";
import RoomCard from "../../../components/community_v4/RoomCard";
import { useAuth } from "../../../context/AuthContext";
import UpsellBanner from "@/components/upsell/UpsellBanner";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
    { slug: "all", label: "All" },
    { slug: "marketing", label: "Marketing" },
    { slug: "seo", label: "SEO" },
    { slug: "sales", label: "Sales" },
    { slug: "growth", label: "Growth" },
    { slug: "ai-tools", label: "AI tools" },
    { slug: "automation", label: "Automation" },
];

export default function RoomsDirectory() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [cat, setCat] = useState("all");
    const [typ, setTyp] = useState("all");
    const [status, setStatus] = useState("live");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [disabled, setDisabled] = useState(false);

    useEffect(() => {
        (async () => {
            setLoading(true); setDisabled(false);
            const params = new URLSearchParams({ status, limit: "24" });
            if (cat !== "all") params.set("category", cat);
            if (typ !== "all") params.set("room_type", typ);
            try {
                const r = await fetch(`${API}/api/community/rooms?${params}`, { credentials: "include" });
                if (r.status === 503) { setDisabled(true); setItems([]); return; }
                if (r.ok) { const d = await r.json(); setItems(d.items || []); }
            } catch {}
            setLoading(false);
        })();
    }, [cat, typ, status]);

    return (
        <div className="min-h-screen bg-[#0A0A0A] text-[#FFFFFF] font-inter" data-testid="rooms-directory-page">
            <header className="border-b border-[#1A1A1A] backdrop-blur-md bg-[#0A0A0A]/80 sticky top-0 z-40">
                <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
                    <Link to="/community" className="text-neutral-400 hover:text-neutral-200 flex items-center gap-1 text-sm">
                        <ArrowLeft size={16} /> Community
                    </Link>
                    <span className="text-neutral-600">·</span>
                    <h1 className="text-base font-semibold">Rooms</h1>
                    <div className="ml-auto flex items-center gap-2">
                        {user && (
                            <button
                                type="button"
                                onClick={() => navigate("/rooms/create")}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-white text-neutral-900 font-semibold rounded-lg text-xs hover:opacity-90"
                                data-testid="rooms-create-btn"
                            >
                                <Plus size={13} /> Create room
                            </button>
                        )}
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 py-6 space-y-4">
                <UpsellBanner surface="community_rooms" />
                {disabled && (
                    <div className="bg-[#FAFAFA]/10 border border-[#FAFAFA]/30 rounded-lg p-4 text-sm text-[#FAFAFA]">
                        Rooms engine disabled.
                    </div>
                )}
                <div className="flex flex-wrap items-center gap-2">
                    <div className="inline-flex items-center gap-1 text-neutral-500 text-xs">
                        <Filter size={13} /> Category:
                    </div>
                    {CATEGORIES.map((c) => (
                        <button
                            key={c.slug}
                            type="button"
                            onClick={() => setCat(c.slug)}
                            className={`px-3 py-1 rounded-full border transition-all text-xs ${cat === c.slug ? "bg-[#FFFFFF]/10 border-[#FFFFFF]/40 text-[#FFFFFF]" : "border-[#1A1A1A] text-neutral-400 hover:border-[#FFFFFF]/30"}`}
                            data-testid={`rooms-cat-${c.slug}`}
                        >
                            {c.label}
                        </button>
                    ))}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <div className="inline-flex items-center gap-1 text-neutral-500 text-xs">Type:</div>
                    <button
                        type="button"
                        onClick={() => setTyp("all")}
                        className={`px-3 py-1 rounded-full border text-xs ${typ === "all" ? "bg-[#CCCCCC]/10 border-[#CCCCCC]/40 text-[#CCCCCC]" : "border-[#1A1A1A] text-neutral-400 hover:border-[#CCCCCC]/30"}`}
                        data-testid="rooms-type-all"
                    >
                        All types
                    </button>
                    <button
                        type="button"
                        onClick={() => setTyp("audio")}
                        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full border text-xs ${typ === "audio" ? "bg-[#FFFFFF]/10 border-[#FFFFFF]/40 text-[#FFFFFF]" : "border-[#1A1A1A] text-neutral-400 hover:border-[#FFFFFF]/30"}`}
                        data-testid="rooms-type-audio"
                    >
                        <Mic size={11} /> Audio
                    </button>
                    <button
                        type="button"
                        onClick={() => setTyp("video")}
                        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full border text-xs ${typ === "video" ? "bg-[#FAFAFA]/10 border-[#FAFAFA]/40 text-[#FAFAFA]" : "border-[#1A1A1A] text-neutral-400 hover:border-[#FAFAFA]/30"}`}
                        data-testid="rooms-type-video"
                    >
                        <Video size={11} /> Video
                    </button>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <div className="inline-flex items-center gap-1 text-neutral-500 text-xs">Status:</div>
                    {["live", "scheduled", "ended"].map((s) => (
                        <button
                            key={s}
                            type="button"
                            onClick={() => setStatus(s)}
                            className={`px-3 py-1 rounded-full border text-xs capitalize ${status === s ? "bg-[#CCCCCC]/10 border-[#CCCCCC]/40 text-[#CCCCCC]" : "border-[#1A1A1A] text-neutral-400 hover:border-[#CCCCCC]/30"}`}
                            data-testid={`rooms-status-${s}`}
                        >
                            {s}
                        </button>
                    ))}
                </div>

                {loading && <p className="text-sm text-neutral-500 italic">Loading…</p>}
                {!loading && !items.length && !disabled && (
                    <p className="text-sm text-neutral-500 italic py-10 text-center">No rooms match your filters</p>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                    {items.map((r) => <RoomCard key={r.id} room={r} />)}
                </div>
            </main>
        </div>
    );
}
